import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import {
  COLOR_SCALE,
  RADIUS_SCALE,
  TYPE_SCALE,
  INTERACTION,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  BACKGROUND_COLOR,
  FONT_WEIGHTS,
  TEXT_TRANSFORM,
  LETTER_SPACING,
  MARGIN_BOTTOM,
  SIZE_SCALE,
  SURFACE_PADDING,
  OVERFLOW,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

interface TooltipContentProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> {
  variant?: 'default' | 'info' | 'warning' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, variant = 'default', size = 'sm', ...props }, ref) => {
  const variants = {
    default: COLOR_SCALE['tooltip-bg'],
    info: 'bg-blue-50 border border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border border-red-200 text-red-800',
    success: 'bg-green-50 border border-green-200 text-green-800',
  };

  const sizes = {
    sm: `${TYPE_SCALE.xs} px-3 py-1.5`,
    md: `${TYPE_SCALE.sm} px-4 py-2`,
    lg: `${TYPE_SCALE.base} px-5 py-3`,
  };

  return (
    <TooltipPrimitive.Portal
      container={typeof document !== 'undefined' ? document.body : undefined}
    >
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          `z-50 overflow-hidden ${RADIUS_SCALE.md} ${sizes[size]} ${variants[variant]} shadow-lg font-normal animate-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
          className
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Contextual tooltip components for common use cases

interface ContextualTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  variant?: 'default' | 'info' | 'warning' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
}

const ContextualTooltip: React.FC<ContextualTooltipProps> = ({
  content,
  children,
  variant = 'default',
  size = 'sm',
  side = 'top',
  delayDuration = 300,
}) => (
  <Tooltip delayDuration={delayDuration}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} variant={variant} size={size}>
      {content}
    </TooltipContent>
  </Tooltip>
);

// Help tooltip for form fields
interface HelpTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({ content, children, side = 'right' }) => (
  <ContextualTooltip content={content} variant="info" size="sm" side={side} delayDuration={500}>
    <div className={`inline-flex items-center ${GAP_SCALE['1']} ${INTERACTION.cursorPointer}`}>
      {children}
      <span
        className="text-muted-foreground hover:text-foreground transition-colors"
        aria-hidden="true"
      >
        ⓘ
      </span>
    </div>
  </ContextualTooltip>
);

// Action hint tooltip
interface ActionTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  shortcut?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

const ActionTooltip: React.FC<ActionTooltipProps> = ({
  content,
  children,
  shortcut,
  side = 'top',
}) => (
  <ContextualTooltip
    content={
      <div className="flex items-center gap-2">
        <span>{content}</span>
        {shortcut && (
          <kbd
            className={`${PADDING_X['1.5']} ${PADDING_Y['0.5']} ${TYPE_SCALE.xs} ${COLOR_SCALE['surface-muted']} ${RADIUS_SCALE.md} ${COLOR_SCALE.border} font-mono`}
          >
            {shortcut}
          </kbd>
        )}
      </div>
    }
    variant="default"
    size="sm"
    side={side}
    delayDuration={200}
  >
    {children}
  </ContextualTooltip>
);

// Validation tooltip for form fields
interface ValidationTooltipProps {
  error?: string;
  warning?: string;
  success?: string;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

const ValidationTooltip: React.FC<ValidationTooltipProps> = ({
  error,
  warning,
  success,
  children,
  side = 'top',
}) => {
  const getContent = () => {
    if (error) return { content: error, variant: 'error' as const };
    if (warning) return { content: warning, variant: 'warning' as const };
    if (success) return { content: success, variant: 'success' as const };
    return null;
  };

  const validation = getContent();

  if (!validation) return <>{children}</>;

  return (
    <ContextualTooltip
      content={validation.content}
      variant={validation.variant}
      size="sm"
      side={side}
      delayDuration={100}
    >
      {children}
    </ContextualTooltip>
  );
};

// Keyboard shortcut display component
interface KeyboardShortcutProps {
  shortcut: string;
  variant?: 'default' | 'subtle';
  size?: 'sm' | 'md';
}

const KeyboardShortcut: React.FC<KeyboardShortcutProps> = ({
  shortcut,
  variant = 'default',
  size = 'sm',
}) => {
  const variants = {
    default: 'bg-muted border border-border text-foreground',
    subtle: 'bg-muted/50 text-muted-foreground',
  };

  const sizes = {
    sm: `${PADDING_X['1.5']} ${PADDING_Y['0.5']} ${TYPE_SCALE.xs}`,
    md: `${PADDING_X['2']} ${PADDING_Y['1']} ${TYPE_SCALE.sm}`,
  };

  return (
    <kbd
      className={cn(
        `${RADIUS_SCALE.md} ${COLOR_SCALE.border} ${FONT_WEIGHTS.mono} ${FONT_WEIGHTS.medium}`,
        variants[variant],
        sizes[size]
      )}
    >
      {shortcut}
    </kbd>
  );
};

// Keyboard shortcuts help component
interface KeyboardShortcutsHelpProps {
  shortcuts: Array<{
    key: string;
    description: string;
    category?: string;
  }>;
  isOpen: boolean;
  onClose: () => void;
}

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({
  shortcuts,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const groupedShortcuts = shortcuts.reduce(
    (acc, shortcut) => {
      const category = shortcut.category ?? 'General';
      acc[category] ??= [];
      acc[category].push(shortcut);
      return acc;
    },
    {} as Record<
      string,
      Array<{
        key: string;
        description: string;
        category?: string;
      }>
    >
  );

  return (
    <div
      className={`fixed inset-0 ${BACKGROUND_COLOR.black[30]} flex items-center justify-center z-modal`}
      onClick={onClose}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      tabIndex={-1}
      role="presentation"
    >
      <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{t('keyboard_shortcuts', 'title', 'Keyboard Shortcuts')}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted transition-colors"
            aria-label={t('keyboard_shortcuts', 'close_hint', 'Close')}
          >
            ✕
          </button>
        </div>
        <div className={`${SURFACE_PADDING['4']} ${OVERFLOW['y-auto']} ${SIZE_SCALE.maxH400}`}>
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category} className={MARGIN_BOTTOM['6']}>
              <h3
                className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.medium} ${COLOR_SCALE['foreground-muted']} ${MARGIN_BOTTOM['3']} ${TEXT_TRANSFORM.uppercase} ${LETTER_SPACING.wide}`}
              >
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div key={index} className="flex items-center justify-between py-1">
                    <span className="text-sm">{shortcut.description}</span>
                    <KeyboardShortcut shortcut={shortcut.key} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  ContextualTooltip,
  HelpTooltip,
  ActionTooltip,
  ValidationTooltip,
  KeyboardShortcut,
  KeyboardShortcutsHelp,
};
